import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';

import { type SplitDrawNameInterface } from 'components/dialogs/draw/splitDraw/AddSplitDrawDialog';
import { sentenceCase } from 'components/utils';
import {
  type AgeCategory,
  Category,
  type EventDivisionData,
  FamilyType,
  Gender,
  type RatingCategory,
  RatingType,
} from 'typedefs/event';

const colors = ['blue', 'red', 'green', 'purple', 'orange', 'yellow', 'pink', 'teal'];

const getRange = (start: number, stop: number) => Array.from({ length: stop - start + 1 }, (_, ind) => start + ind);

const letters = getRange('A'.charCodeAt(0), 'Z'.charCodeAt(0)).map((x) => String.fromCharCode(x));

export const getDrawNames = (n: number, name: SplitDrawNameInterface) => {
  if (name.customName === 'color') {
    return colors.map(sentenceCase);
  }

  const customName = name.customName || 'Flight';

  if (name.customNameOptions === 'letters') {
    const length = Math.min(n, letters.length);

    return Array.from({ length }).map((_, ind) => `${customName} ${letters[ind]}`);
  }

  return Array.from({ length: n }).map((_, ind) => `${customName} ${ind + 1}`);
};

const mapEventType = (eventType?: EventTypeEnum) => {
  switch (eventType) {
    case EventTypeEnum.SINGLES:
      return 'Singles';
    case EventTypeEnum.DOUBLES:
      return 'Doubles';
    case EventTypeEnum.TEAM:
      return 'Team';
    default:
      return '';
  }
};

export const mapEventShortCodeGender = (tournamentCategory?: Category, gender?: Gender, wheelchairRating?: string) => {
  if (tournamentCategory === Category.JUNIOR) {
    return gender !== Gender.MIXED ? gender?.[0] : 'X';
  } else {
    return (
      ((gender === Gender.BOYS || gender === Gender.MALE) && 'M') ||
      (gender === Gender.GIRLS && 'W') ||
      (gender === Gender.FEMALE && 'F') ||
      (gender === Gender.MIXED && 'X') ||
      `C${wheelchairRating ?? ''}`
    );
  }
};

export const mapEventShortCodeAge = (isRating: boolean, ageCategory?: AgeCategory) => {
  const isOpen = ageCategory?.todsCode === 'OPEN';
  const minAge = ageCategory?.minimumAge;
  const maxAge = ageCategory?.maximumAge;
  let result = '';

  if (isOpen) {
    if (!isRating) result = 'Open';
  } else if (minAge && !maxAge) {
    result = `${isRating ? ', age' : ''} ${minAge} O`;
  } else if (maxAge && !minAge) {
    result = `${isRating ? ', age' : ''} ${maxAge} U`;
  } else if (minAge && maxAge) {
    result = `${isRating ? ', age' : ''} ${minAge} - ${maxAge}`;
  }

  return result;
};

const mapEventFamilyCode = (type?: FamilyType) => {
  switch (type) {
    case FamilyType.FATHER_SON:
      return 'F/S';
    case FamilyType.MOTHER_SON:
      return 'M/S';
    case FamilyType.GRANDFATHER_GRANDSON:
      return 'GF/GS';
    case FamilyType.MOTHER_DAUGHTER:
      return 'M/D';
    case FamilyType.FATHER_DAUGHTER:
      return 'F/D';
    case FamilyType.HUSBAND_WIFE:
      return 'H/W';
    case FamilyType.SAME_GENDER_MALE:
      return 'SGMC';
    case FamilyType.SAME_GENDER_FEMALE:
      return 'SGFC';
    default:
      return '';
  }
};

export const mapEventShortCodeRating = (isRating: boolean, ratingCategory?: RatingCategory) => {
  return isRating
    ? (ratingCategory?.ratingType === RatingType.WTN &&
        ` ${ratingCategory?.ratingType ?? ''} ${ratingCategory?.minimumValue ?? ''} - ${
          ratingCategory?.maximumValue ?? ''
        }`) ||
        ` ${ratingCategory?.ratingType ?? ''} ${
          (ratingCategory?.value && Number(ratingCategory?.value).toFixed(1)) || ''
        }`
    : '';
};

export const mapEventShortCode = (eventDivisionData?: EventDivisionData) => {
  const isRating = Object.values(eventDivisionData?.ratingCategory ?? {}).length > 0;
  const isFamily = eventDivisionData?.familyType;
  const gender = mapEventShortCodeGender(
    eventDivisionData?.tournamentCategory,
    eventDivisionData?.gender,
    eventDivisionData?.wheelchairRating,
  );
  const age = mapEventShortCodeAge(isRating, eventDivisionData?.ageCategory);
  const rating = mapEventShortCodeRating(isRating, eventDivisionData?.ratingCategory);
  const eventType = mapEventType(eventDivisionData?.eventType);
  const familyCode = `${mapEventFamilyCode(eventDivisionData?.familyType)} ${age} ${eventType}`;

  const code = isFamily
    ? familyCode
    : `${gender} ${isRating ? eventType : ''} ${rating}${age} ${!isRating ? eventType : ''}`;
  // convert multiple consecutive whitespace down to 1
  return code.replace(/\s+/g, ' ');
};
